import { useEffect } from 'react';
import {
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  TextFormatType,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isMentionNode, isMentionFormat } from './mention-node';

/**
 * Lets the bold / italic / underline / strikethrough toolbar buttons format a
 * mention. A mention is a `DecoratorNode`, so Lexical's built-in
 * `FORMAT_TEXT_COMMAND` handler — which only mutates `TextNode`s in a
 * `RangeSelection` — skips it entirely. This higher-priority handler toggles
 * the format on any selected mention(s), then:
 *
 *  - returns `true` for a NodeSelection (a clicked mention): there is no text
 *    for the default handler to act on, so we fully own the keystroke;
 *  - returns `false` for a RangeSelection that also spans plain text, so the
 *    default handler still formats the text — the mention just stays in sync.
 *
 * Direction (add vs remove) mirrors what the default handler will apply to the
 * text in the same selection, so a mixed range toggles consistently.
 *
 * Registered unconditionally (not gated by EE.TagUser.Enabled) so mentions
 * loaded from saved content can always be formatted, even where creating new
 * mentions is disabled. Exported for unit testing.
 */
export function registerMentionFormatting(editor: LexicalEditor): () => void {
  return editor.registerCommand<TextFormatType>(
    FORMAT_TEXT_COMMAND,
    (format) => {
      if (!isMentionFormat(format)) return false;
      const selection = $getSelection();

      if ($isNodeSelection(selection)) {
        const mentions = selection.getNodes().filter($isMentionNode);
        if (mentions.length === 0) return false;
        const allActive = mentions.every((m) => m.hasFormat(format));
        mentions.forEach((m) => m.toggleFormat(format, !allActive));
        return true;
      }

      if ($isRangeSelection(selection)) {
        const mentions = selection.getNodes().filter($isMentionNode);
        if (mentions.length === 0) return false;
        const willActivate = !selection.hasFormat(format);
        mentions.forEach((m) => m.toggleFormat(format, willActivate));
        return false;
      }

      return false;
    },
    COMMAND_PRIORITY_LOW
  );
}

const MentionFormatPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => registerMentionFormatting(editor), [editor]);
  return null;
};

export default MentionFormatPlugin;
